import React, { useEffect, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import Modal from "react-bootstrap/Modal";
import { App } from "./App";
import { request, Schedule, propertyDate, taskCounts } from "./api";
export async function mount(target: HTMLElement) {
  createRoot(target).render(<App />);
}
function Toolbar() {
  const [data, setData] = useState<Schedule | null>(null),
    [error, setError] = useState(""),
    [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = () =>
      request<Schedule>(`/schedule?days=1`)
        .then((d) => {
          if (alive) {
            setData(d);
            setError("");
          }
        })
        .catch((e) => {
          if (alive) setError(e.message);
        });
    void load();
    window.addEventListener("housekeeping-changed", load);
    return () => {
      alive = false;
      window.removeEventListener("housekeeping-changed", load);
    };
  }, []);
  const counts = data ? taskCounts(data.rooms) : null;
  return (
    <>
      <button
        className="btn btn-outline-contrast"
        onClick={() => setOpen(true)}
        title={error || "Daily housekeeping tasks"}
      >
        Housekeeping{" "}
        {counts ? (
          <>
            <span className="badge bg-danger ms-1" title="Overdue">
              {counts.overdue}
            </span>
            <span className="badge bg-primary ms-1" title="To do">
              {counts.todo}
            </span>
            <span className="badge bg-success ms-1" title="Done">
              {counts.done}
            </span>
          </>
        ) : error ? (
          <span className="badge bg-danger">!</span>
        ) : (
          <span
            className="spinner-border spinner-border-sm ms-1"
            role="status"
          />
        )}
      </button>
      <Modal
        show={open}
        onHide={() => setOpen(false)}
        size="lg"
        aria-label="Daily housekeeping"
      >
        <Modal.Body className="p-0">
          <App compact onClose={() => setOpen(false)} />
        </Modal.Body>
      </Modal>
    </>
  );
}
// One document lifecycle listener handles initial HTML, server renders and enhanced navigation.
// Removed hosts are unmounted, including their portalled dialogs and listeners.
export function initialize() {
  const mounted = new Map<
    HTMLElement,
    { root: Root; target: HTMLElement; toolbar: boolean }
  >();
  const reconcile = () => {
    for (const [host, entry] of mounted)
      if (!host.isConnected) {
        entry.root.unmount();
        if (entry.toolbar) entry.target.remove();
        mounted.delete(host);
      }
    document
      .querySelectorAll<HTMLElement>(
        "[data-housekeeping-app], [data-housekeeping-toolbar]",
      )
      .forEach((host) => {
        if (mounted.has(host)) return;
        const isToolbar = host.hasAttribute("data-housekeeping-toolbar");
        const target = isToolbar ? document.createElement("span") : host;
        if (isToolbar) target.className = "ms-2";
        const root = createRoot(target);
        mounted.set(host, { root, target, toolbar: isToolbar });
        root.render(isToolbar ? <Toolbar /> : <App />);
      });
    for (const entry of mounted.values())
      if (entry.toolbar) {
        const hrm = document.getElementById("hrm-status-btn");
        if (hrm && entry.target.previousElementSibling !== hrm)
          hrm.insertAdjacentElement("afterend", entry.target);
      }
  };
  const observer = new MutationObserver(reconcile);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  reconcile();
  return () => {
    observer.disconnect();
    for (const entry of mounted.values()) {
      entry.root.unmount();
      if (entry.toolbar) entry.target.remove();
    }
    mounted.clear();
  };
}
